import React from 'react'
import {
  IProps as IFeatureGrid,
  ILayout,
  IImageLayout,
  IVideoLayout,
  FeatureGrid,
} from 'marketing-site/src/library/components/FeatureGrid'

import {
  IFeatureGrid as IContentfulFeatureGrid,
  IFeatureGridAlignment as IContentfulFeatureGridAlignment,
  IFeatureGridImage as IContentfulFeatureGridImage,
  IHeroVideo as IContentfulHeroVideo,
} from 'marketing-site/@types/generated/contentful'

import { transformCTAWithBackground } from 'marketing-site/src/transformers/elements/ContentfulCTALink'
import { transformGridItem } from 'marketing-site/src/transformers/elements/ContentfulGridItem'
import { transformImage } from 'marketing-site/src/transformers/elements/ContentfulImage'
import EntryMarker from 'marketing-site/src/components/common/EntryMarker'
import { getHexColorFromName } from 'marketing-site/src/library/utils'
import { IVideo } from 'marketing-site/src/library/components/HeroWithCTA'

export const ContentfulFeatureGrid = (gridItem: IContentfulFeatureGrid): React.ReactElement => (
  <EntryMarker entry={gridItem}>
    {() => <FeatureGrid {...transformFeatureGridEntry(gridItem)} />}
  </EntryMarker>
)

const transformVideo = (videos: IContentfulHeroVideo): IVideo => {
  const videosWithBreakpoints = Array.isArray(videos.fields.videoWithBreakpoints)
    ? videos.fields.videoWithBreakpoints.map((video) => ({
        video: video.fields.video.fields.file.url,
        fallbackImage: video.fields.fallbackImage.fields.image.fields.file.url,
        breakpoint: video.fields.breakpoint,
      }))
    : undefined

  return {
    fallbackImage: videos.fields.fallbackImage.fields.file.url,
    isBackground: videos.fields.isBackground,
    video: videos.fields.video.fields.file.url,
    videosWithBreakpoints,
    videoWebm: videos.fields.videoWebm && videos.fields.videoWebm.fields.file.url,
    playOnce: videos.fields.notLooping,
  }
}

function getLayoutFromData(
  layout: IContentfulFeatureGridAlignment | IContentfulFeatureGridImage | IContentfulHeroVideo,
): ILayout | IImageLayout | IVideoLayout {
  if (layout.sys.contentType.sys.id === 'featureGridImage') {
    return {
      centerAlign: false,
      imageRef: transformImage((layout as IContentfulFeatureGridImage).fields.featureGridImgRef),
    }
  }

  if (layout.sys.contentType.sys.id === 'heroVideo') {
    return {
      centerAlign: false,
      videoRef: transformVideo(layout as IContentfulHeroVideo),
    }
  }

  return {
    centerAlign: (layout as IContentfulFeatureGridAlignment).fields.centerAlign,
  }
}

export function transformFeatureGridEntry({ fields }: IContentfulFeatureGrid): IFeatureGrid {
  return {
    ...fields,
    title: fields.title && fields.title.trim(),
    cta: fields.cta && transformCTAWithBackground(fields.cta),
    layout: getLayoutFromData(fields.layout),
    items: fields.items.map((data) =>
      transformGridItem(data, getLayoutFromData(fields.layout).centerAlign),
    ),
    bgColor: getHexColorFromName(fields.bgColor || 'White'),
    leftAlignImage: fields.leftAlignImage || false,
  }
}
